import React from "react";
import {
  getUser,
  readStoredUser,
  User,
  setUserToken,
  getUserToken,
  logout as providerLogout,
} from "../auth-provider";
import ApiClient from "../utils/api-client";

interface AuthContextProps {
  user: User | null;
  loginUser: (token: string) => Promise<void>;
  logout: () => void;
}
const AuthContext = React.createContext<AuthContextProps>({
  user: null,
  loginUser: async (token: string) => {},
  logout: () => {},
});
AuthContext.displayName = "AuthContext";

function AuthProvider(props: any) {
  // Read the stored token during the first render so a reload does not paint
  // the login page before the session is known.
  const [user, setUser] = React.useState<User | null>(() => readStoredUser());

  const loginUser = React.useCallback(async (token: string) => {
    setUserToken(token);
    const user = await getUser();
    setUser(user);
  }, []);

  const logout = React.useCallback(() => {
    providerLogout();
    setUser(null);
  }, []);

  const refreshSession = React.useCallback(() => {
    if (!getUserToken()) {
      return;
    }
    ApiClient.refreshSession()
      .then((res) => {
        if (res?.data?.token) {
          return loginUser(res.data.token);
        }
        return undefined;
      })
      .catch(() => {
        // A 401 is handled by the API client. Other failures keep the current token.
      });
  }, [loginUser]);

  React.useEffect(() => {
    refreshSession();
    const onFocus = () => refreshSession();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshSession]);

  return (
    <AuthContext.Provider value={{ user, loginUser, logout }} {...props} />
  );
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuth };
